import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { ErrorMessage } from '../styled/ErrorMessage';
import { useTranslation } from "react-i18next";
import { fetchLanguages } from "../context/LanguageUtility";
import { MdRefresh } from "react-icons/md";

const { getRandomInteraction } = require('../context/ConversationScenarios');

const ModalBackground = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
`;

const ModalContent = styled.div`
    background: rgba(30, 27, 75, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    backdrop-filter: blur(16px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    padding: 2em;
    max-width: 460px;
    width: 90%;
    position: relative;
    color: rgba(255, 255, 255, 0.85);
`;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
`;

const Label = styled.label`
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 500;
`;

const InputTitle = styled.div`
    font-size: 1.2em;
    font-weight: 600;
    color: white;
    margin-bottom: 0.5rem;
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const StyledInput = styled.input`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.7rem 2.5rem 0.7rem 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
    transition: border-color 0.2s, background 0.2s;

    &::placeholder { color: rgba(255, 255, 255, 0.25); }
    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(255, 255, 255, 0.08);
    }
`;

const RefreshIcon = styled.span`
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    cursor: pointer;
    color: rgba(255, 255, 255, 0.35);
    font-size: 1.4rem;
    transition: color 0.2s;
    display: flex;

    &:hover { color: rgba(255, 255, 255, 0.7); }
`;

const Select = styled.select`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.7em 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
    -webkit-appearance: none;
    transition: border-color 0.2s;

    &:focus { outline: none; border-color: rgba(99, 102, 241, 0.5); }
    & option { background: #1a1a2e; color: white; }

    @media (max-width: 768px) { padding: 1rem; }
`;

const CheckboxRow = styled.label`
    display: flex;
    align-items: center;
    gap: 0.7rem;
    cursor: pointer;
    margin-top: 0.3rem;
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #6366f1;
`;

const CheckboxLabel = styled.span`
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
`;

const HelpText = styled.p`
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.4);
    line-height: 1.5;
    font-style: italic;
    margin: 0;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 0.6rem;
    justify-content: center;
    margin-top: 0.5rem;
`;

const ActionButton = styled.button`
    padding: 0.55rem 1.6rem;
    border-radius: 12px;
    border: none;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;

    background: ${props => props.$primary
        ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
        : 'rgba(255, 255, 255, 0.08)'};
    color: ${props => props.$primary ? 'white' : 'rgba(255, 255, 255, 0.6)'};
    border: ${props => props.$primary ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'};
    box-shadow: ${props => props.$primary ? '0 3px 14px rgba(99, 102, 241, 0.3)' : 'none'};

    &:hover {
        transform: translateY(-1px);
        background: ${props => props.$primary
            ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
            : 'rgba(255, 255, 255, 0.12)'};
    }
    &:active { transform: translateY(0); }
`;

const SpinnerOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(30, 27, 75, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 20px;
`;

const Spinner = styled.div`
    border: 3px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top: 3px solid #a5b4fc;
    width: 36px;
    height: 36px;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const RoomForm = ({ isOpen, onNewRoom, onClose }) => {
    const [name, setName] = useState("");
    const [language, setLanguage] = useState("default");
    const [isLanguageEnabled, setIsLanguageEnabled] = useState(false);
    const [customLanguage, setCustomLanguage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [supportedLanguages, setSupportedLanguages] = useState([]);
    const [languageConfiguration, setLanguageConfiguration] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        fetchLanguages(setSupportedLanguages, setLanguageConfiguration);
        setName(getRandomInteraction());
    }, []);

    const checkCustomLanguage = async (language) => {
        const response = await axios.post(`${process.env.REACT_APP_SERVER}/lang/c`, { customLanguage: language }, { withCredentials: true });
        if (!response.data.success) {
            if (response.data.suggestion) {
                setErrorMessage(`Did you mean '${response.data.suggestion}'?`);
            } else {
                setErrorMessage("Sorry we don't recognize that as a language.");
            }
        }
        return response.data.success;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (isLanguageEnabled && language === "default") {
            setErrorMessage("Please select a language.");
            setIsSubmitting(false);
            return;
        }

        if (language === 'Other') {
            try {
                const isValid = await checkCustomLanguage(customLanguage);
                if (!isValid) { setIsSubmitting(false); return; }
            } catch (error) {
                setErrorMessage("Sorry we don't recognize that as a language.");
                setIsSubmitting(false);
                return;
            }
        }

        const newRoom = {
            name: name,
            description: '',
            withBot: isLanguageEnabled,
            botLang: isLanguageEnabled ? (language === 'Other' ? (customLanguage.charAt(0).toUpperCase() + customLanguage.slice(1)) : language) : undefined
        };

        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER}/rooms`, newRoom, { withCredentials: true });
            if (response.data.success) {
                onNewRoom(response.data.room);
                setName(getRandomInteraction());
                setIsSubmitting(false);
                clearForm();
            } else {
                setErrorMessage(response.data.error);
                setIsSubmitting(false);
            }
        } catch (err) {
            setErrorMessage("Something went wrong, unable to create conversation.");
            setIsSubmitting(false);
        }
    };

    const clearForm = () => {
        setName(getRandomInteraction());
        setLanguage("default");
        setIsLanguageEnabled(false);
        setCustomLanguage("");
        setErrorMessage("");
        onClose();
    };

    return (
        isOpen && (
            <ModalBackground onClick={onClose}>
                <ModalContent onClick={e => e.stopPropagation()}>
                    {isSubmitting && <SpinnerOverlay><Spinner /></SpinnerOverlay>}
                    {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

                    <StyledForm onSubmit={handleSubmit}>
                        <FormGroup>
                            <InputTitle>{t('StartBabblingAbout')}</InputTitle>
                            <InputWrapper>
                                <StyledInput
                                    type="text"
                                    placeholder={t('RoomFormName')}
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    maxLength={50}
                                />
                                <RefreshIcon onClick={() => setName(getRandomInteraction())}>
                                    <MdRefresh />
                                </RefreshIcon>
                            </InputWrapper>
                        </FormGroup>

                        <FormGroup>
                            <CheckboxRow>
                                <StyledCheckbox
                                    checked={isLanguageEnabled}
                                    onChange={e => setIsLanguageEnabled(e.target.checked)}
                                />
                                <CheckboxLabel>{t('RoomFormAddBab')}</CheckboxLabel>
                            </CheckboxRow>
                            <HelpText>{t('RoomFormBabDesc')}</HelpText>
                        </FormGroup>

                        {isLanguageEnabled && (
                            <FormGroup>
                                <Select
                                    value={language}
                                    onChange={e => { setLanguage(e.target.value); setErrorMessage(""); }}
                                >
                                    <option value="default">{t('LanguageSelectDefault')}</option>
                                    {supportedLanguages.map((lang, index) => (
                                        <option key={index} value={lang}>{t(lang)}</option>
                                    ))}
                                    <option value="Other">Other</option>
                                </Select>
                            </FormGroup>
                        )}

                        {(language === 'Other' && isLanguageEnabled) && (
                            <FormGroup>
                                <StyledInput
                                    type="text"
                                    placeholder={t('SpecifyLanguage')}
                                    value={customLanguage}
                                    onChange={e => setCustomLanguage(e.target.value)}
                                    required={language === 'Other'}
                                />
                            </FormGroup>
                        )}

                        <ButtonContainer>
                            <ActionButton type="button" onClick={clearForm}>{t('CancelButton')}</ActionButton>
                            <ActionButton $primary type="submit">{t('RoomCreateButton')}</ActionButton>
                        </ButtonContainer>
                    </StyledForm>
                </ModalContent>
            </ModalBackground>
        )
    );
};

export default RoomForm;
